// =============================================================================
// SalesOrderInspectorModal — read one Unleashed Sales Order's raw JSON (modal)
// =============================================================================
// Opened from the Sales Orders screen ("Inspect Sales Orders on Unleashed"). Reads a
// real order back from Unleashed via unleashed-fetch { resource:"SalesOrders",
// orderNumber } and shows the raw JSON so we can see the exact field shapes (e.g.
// delivery / SalesOrderGroup). Read-only — nothing is written. Moved out of the
// (retiring) Sync Utilities screen (John, 2 Aug 2026). Fixed (non-draggable) modal.
// =============================================================================
import React from "react";
import { getSupabaseClient } from "../../utils/supabase";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

export function SalesOrderInspectorModal({ onClose }) {
  const [orderNumber, setOrderNumber] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [json, setJson] = React.useState("");

  async function fetchOrder() {
    setBusy(true);
    setError("");
    setJson("");
    try {
      const supa = getSupabaseClient();
      if (!supa) {
        setError("Could not reach the server.");
        return;
      }
      const { data, error: invErr } = await supa.functions.invoke("unleashed-fetch", {
        body: { resource: "SalesOrders", orderNumber: orderNumber.trim() },
      });
      if (invErr) {
        setError(`Fetch failed: ${invErr.message}`);
        return;
      }
      if (!data || data.ok === false) {
        const st = data?.status ? ` (${data.status})` : "";
        setError(`Unleashed error: ${data?.error || "unknown"}${st}`);
        return;
      }
      const first = (data.items || [])[0];
      if (!first) {
        setError("No order found with that number in Unleashed.");
        return;
      }
      setJson(JSON.stringify(first, null, 2));
    } catch (e) {
      setError(`Failed: ${e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalBackdrop
      onClose={onClose}
      portal
      contentStyle={{ maxWidth: 720, width: "92vw", background: "#fff", borderRadius: 10 }}
    >
      <div style={{ padding: 20, maxHeight: "80vh", overflow: "auto", position: "relative" }}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 8,
            right: 10,
            background: "transparent",
            border: "none",
            fontSize: 22,
            lineHeight: 1,
            color: "#607D79",
            cursor: "pointer",
          }}
        >
          ×
        </button>
        <h3 style={{ margin: "0 0 6px", fontSize: 16, color: "#00695C" }}>
          Inspect a Sales Order (raw Unleashed JSON)
        </h3>
        <p style={{ margin: "0 0 14px", fontSize: 13, color: "#607D79" }}>
          Read a real order back from Unleashed to see its exact field shapes (e.g. delivery / sales
          group). Read-only — nothing is changed.
        </p>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <input
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            placeholder="e.g. SO-00016851"
            style={{
              border: "1px solid #CFD8DC",
              borderRadius: 6,
              padding: "8px 10px",
              fontSize: 13,
              fontFamily: "inherit",
              width: 220,
            }}
          />
          <Btn onClick={fetchOrder} disabled={busy || !orderNumber.trim()}>
            {busy ? "Fetching…" : "Fetch order JSON"}
          </Btn>
          <button
            type="button"
            onClick={function () {
              setOrderNumber("");
              setJson("");
              setError("");
            }}
            disabled={busy || (!orderNumber && !json && !error)}
            style={{
              background: "white",
              color: "#607D79",
              border: "1px solid #CFD8DC",
              borderRadius: 6,
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: "inherit",
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            Clear
          </button>
          {error && (
            <span style={{ fontSize: 13, fontWeight: 600, color: "#C62828" }}>{error}</span>
          )}
        </div>
        {json && (
          <pre
            style={{
              marginTop: 14,
              fontSize: 12,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              background: "#F7F9F9",
              border: "1px solid #E0EDEB",
              borderRadius: 8,
              padding: "12px 14px",
              maxHeight: 420,
              overflow: "auto",
              userSelect: "all",
            }}
          >
            {json}
          </pre>
        )}
      </div>
    </ModalBackdrop>
  );
}
